import { Icon } from "./components/Icon";
import { MapSection } from "./components/MapSection";
import { StickyCta } from "./components/StickyCta";
import { useReveal } from "./useReveal";
import {
  CONTACT,
  COORDINATES,
  DIRECTIONS_URL,
  INCLUDED,
  LANDMARKS,
  LEASE_YEARS,
  SITE,
  SITE_ELEVATION_APPROX,
  TOWER_HEIGHT_METERS,
} from "./site";
import { useEffect, useState } from "react";

function TowerMark() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
        <path d="M9.4 21 12 3l2.6 18" />
        <path d="M10.2 15.5h3.6M9.7 18.2h4.6M10.7 12.8h2.6" />
        <path d="m10.6 10 2.8 2.8M13.4 10l-2.8 2.8" />
      </g>
    </svg>
  );
}

function Hero() {
  return (
    <section className="hero">
      <img
        className="hero-photo"
        src="/photos/torre-42m.jpg"
        alt="Torre arriostrada de 42 metros en El Boquerón, vista desde su base"
        width={1536}
        height={1536}
        fetchPriority="high"
        decoding="async"
      />
      <div className="hero-content">
        <p className="hero-eyebrow">{SITE.place}</p>
        <h1>
          Torre de <em>42&nbsp;metros</em> en&nbsp;alquiler
        </h1>
        <p className="hero-lead">
          Espacio disponible en una torre que ya está operando, en el acceso
          principal al cráter de El&nbsp;Boquerón.
        </p>
        <div className="hero-actions">
          <a className="button button--solid" href={CONTACT.tel}>
            <Icon name="phone" />
            Llamar ahora
          </a>
          <a
            className="button button--outline"
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="whatsapp" />
            WhatsApp
          </a>
        </div>
        <dl className="hero-stats">
          <div>
            <dt>Torre</dt>
            <dd>{TOWER_HEIGHT_METERS} m</dd>
          </div>
          <div>
            <dt>Altitud</dt>
            <dd>≈{SITE_ELEVATION_APPROX.toLocaleString("es-SV")} m</dd>
          </div>
          <div>
            <dt>Contrato</dt>
            <dd>{LEASE_YEARS} años</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

function Included() {
  const reveal = useReveal();
  return (
    <section className="section" ref={reveal}>
      <h2>
        Qué incluye <span>el alquiler</span>
      </h2>
      <ul className="included">
        {INCLUDED.map((item) => (
          <li key={item.icon}>
            <span className="included-icon" aria-hidden="true">
              <Icon name={item.icon} />
            </span>
            {item.label}
          </li>
        ))}
      </ul>
      <p className="section-note">
        El predio está operando y vigilado. Se firma contrato a {LEASE_YEARS} años.
      </p>
    </section>
  );
}

type CopyState = "idle" | "done" | "failed";

const COPY_LABEL: Record<CopyState, string> = {
  idle: "Copiar",
  done: "Copiado",
  failed: "No se pudo",
};

function Location() {
  const reveal = useReveal();
  const [copyState, setCopyState] = useState<CopyState>("idle");

  useEffect(() => {
    if (copyState === "idle") return;
    const timer = window.setTimeout(() => setCopyState("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  const copyCoordinates = () => {
    navigator.clipboard.writeText(COORDINATES).then(
      () => setCopyState("done"),
      () => setCopyState("failed"),
    );
  };

  return (
    <section className="section" ref={reveal}>
      <h2>
        Dónde <span>está</span>
      </h2>
      <ul className="landmarks">
        {LANDMARKS.map((landmark) => (
          <li key={landmark}>
            <Icon name="pin" />
            {landmark}
          </li>
        ))}
      </ul>
      <div className="coordinates">
        <div>
          <span>WGS 84</span>
          <strong>{COORDINATES}</strong>
        </div>
        <button type="button" onClick={copyCoordinates} data-state={copyState} aria-live="polite">
          {COPY_LABEL[copyState]}
        </button>
      </div>
      <a className="button button--dark" href={DIRECTIONS_URL} target="_blank" rel="noreferrer">
        Cómo llegar
        <Icon name="external" />
      </a>
    </section>
  );
}

function AltOffer() {
  const reveal = useReveal();
  return (
    <section className="section alt-offer" ref={reveal}>
      <img
        src="/photos/torre-vientos.jpg"
        alt="Predio con torres de telecomunicaciones instaladas"
        width={906}
        height={1612}
        loading="lazy"
        decoding="async"
      />
      <div>
        <h2>
          ¿Prefiere instalar <span>su propia torre?</span>
        </h2>
        <p>
          También se alquila espacio en el mismo predio para que el operador
          monte una torre autosoportable, con suelo firme y energía cerca.
        </p>
        <a className="button button--solid" href={CONTACT.whatsapp} target="_blank" rel="noreferrer">
          <Icon name="whatsapp" />
          Consultar disponibilidad
        </a>
      </div>
    </section>
  );
}

function Contact() {
  const reveal = useReveal();
  return (
    <section className="contact" ref={reveal} id="contacto">
      <p className="contact-kicker">¿Le interesa el sitio?</p>
      <a className="contact-phone" href={CONTACT.tel}>
        {CONTACT.display}
      </a>
      <p className="contact-copy">
        Llame o escriba por WhatsApp y coordinamos una visita al predio.
      </p>
      <div className="hero-actions">
        <a className="button button--solid" href={CONTACT.tel}>
          <Icon name="phone" />
          Llamar
        </a>
        <a
          className="button button--outline"
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noreferrer"
        >
          <Icon name="whatsapp" />
          WhatsApp
        </a>
      </div>
    </section>
  );
}

export default function App() {
  return (
    <>
      <header className="app-header">
        <span className="brand">
          <span className="brand-mark" aria-hidden="true">
            <TowerMark />
          </span>
          <b>{SITE.name}</b>
        </span>
        <a className="header-call" href={CONTACT.tel}>
          <Icon name="phone" />
          <span>{CONTACT.display}</span>
        </a>
      </header>

      <main>
        <Hero />
        <MapSection />
        <Included />
        <Location />
        <AltOffer />
        <Contact />
      </main>

      <footer className="app-footer">
        <span>{SITE.name} · {SITE.place}</span>
      </footer>

      <StickyCta />

      <a
        className="wa-float"
        href={CONTACT.whatsapp}
        target="_blank"
        rel="noreferrer"
        aria-label="Escribir por WhatsApp"
      >
        <Icon name="whatsapp" />
      </a>
    </>
  );
}
